import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Plus, Search, UploadCloud, Edit2, ToggleLeft, ToggleRight } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Badge } from "shared/ui/badge"
import { Switch } from "shared/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"
import {
  PlotPlanDto,
  PlotPlanStatusFilter,
  PlotPlanUpsertRequest,
  createPlotPlan,
  searchPlotPlans,
  updatePlotPlan,
  uploadPlotPlanVersion,
  fetchPlotPlanVersions,
  fetchWorkAreas,
  WorkAreaOption,
  PlotPlanVersionDto,
} from "modules/admin/api/plotPlanService"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? ""

interface PlotPlanMasterProps {
  currentUser?: { tenantId?: string; name?: string }
}

type PlotPlanFormState = {
  workArea: string
  name: string
  description: string
  isActive: boolean
  versionName: string
  remark: string
  file: File | null
}

const initialFormState: PlotPlanFormState = {
  workArea: "",
  name: "",
  description: "",
  isActive: true,
  versionName: "",
  remark: "",
  file: null,
}

const getFileName = (path?: string | null) => {
  if (!path) return ""
  const cleanPath = path.split("?")[0]
  const parts = cleanPath.split("/")
  return parts[parts.length - 1] ?? ""
}

export function PlotPlanMaster({ currentUser }: PlotPlanMasterProps) {
  const tenantId = currentUser?.tenantId ?? DEFAULT_TENANT_ID

  const [items, setItems] = useState<PlotPlanDto[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [workAreaFilter, setWorkAreaFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState<PlotPlanStatusFilter>("all")
  const [workAreaFilterSearch, setWorkAreaFilterSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const [formState, setFormState] = useState<PlotPlanFormState>(initialFormState)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingItem, setEditingItem] = useState<PlotPlanDto | null>(null)
  const [workAreas, setWorkAreas] = useState<WorkAreaOption[]>([])
  const [workAreaFormSearch, setWorkAreaFormSearch] = useState("")
  const [plotPlanVersions, setPlotPlanVersions] = useState<PlotPlanVersionDto[]>([])
  const [isVersionsLoading, setIsVersionsLoading] = useState(false)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])
  const filteredWorkAreasFilter = useMemo(() => {
    const term = workAreaFilterSearch.trim().toLowerCase()
    if (!term) return workAreas
    return workAreas.filter((area) => `${area.code} ${area.name}`.toLowerCase().includes(term))
  }, [workAreas, workAreaFilterSearch])

  const filteredWorkAreasForm = useMemo(() => {
    const term = workAreaFormSearch.trim().toLowerCase()
    if (!term) return workAreas
    return workAreas.filter((area) => `${area.code} ${area.name}`.toLowerCase().includes(term))
  }, [workAreas, workAreaFormSearch])


  const handlePageSizeChange = (value: string) => {
    const nextPageSize = Number(value)
    if (Number.isNaN(nextPageSize)) return
    setPageSize(nextPageSize)
    setPage(1)
  }

  const loadData = useCallback(async () => {
    if (!tenantId) return
    setIsLoading(true)
    try {
      const response = await searchPlotPlans({
        tenantId,
        search: searchTerm.trim() || undefined,
        workArea: workAreaFilter.trim() || undefined,
        status: statusFilter,
        page,
        pageSize,
      })
      const safeItems = response.items ?? []
      setItems(safeItems)
      setTotalItems(response.totalItems ?? safeItems.length)
    } catch (error) {
      console.error(error)
      toast.error("โหลดข้อมูล Plot Plan ไม่ได้")
    } finally {
      setIsLoading(false)
    }
  }, [page, pageSize, searchTerm, workAreaFilter, statusFilter, tenantId])

  useEffect(() => {
    setPage(1)
  }, [searchTerm, workAreaFilter, statusFilter])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    const loadWorkAreas = async () => {
      if (!tenantId) return
      try {
        const areas = await fetchWorkAreas(tenantId)
        setWorkAreas(areas)
      } catch (error) {
        console.error("Failed to load work areas", error)
      }
    }
    loadWorkAreas()
  }, [tenantId])

  const resetForm = () => {
    setFormState(initialFormState)
    setEditingItem(null)
    setPlotPlanVersions([])
    setWorkAreaFormSearch("")
  }

  const handleDialogChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) {
      resetForm()
    }
  }

  const handleOpenCreate = () => {
    resetForm()
    setIsDialogOpen(true)
  }

  const handleOpenEdit = (item: PlotPlanDto) => {
    setEditingItem(item)
    setFormState({
      workArea: item.workAreaCode ?? "",
      name: item.name ?? "",
      description: item.description ?? "",
      isActive: item.isActive,
      versionName: "",
      remark: "",
      file: null,
    })
    setIsDialogOpen(true)
    setIsVersionsLoading(true)
    fetchPlotPlanVersions(tenantId, item.plotPlanId)
      .then((versions) => {
        setPlotPlanVersions(versions ?? [])
      })
      .catch((error) => {
        console.error("Failed to load plot plan versions", error)
        setPlotPlanVersions([])
      })
      .finally(() => {
        setIsVersionsLoading(false)
      })
  }

  const handleSubmit = async () => {
    if (!tenantId) return
    if (!formState.name.trim()) {
      toast.error("กรอกชื่อแผนผังให้ครบ")
      return
    }
    if (!formState.workArea.trim()) {
      toast.error("กรุณาเลือก Work Area")
      return
    }
    setIsSubmitting(true)
    try {
      const payload: PlotPlanUpsertRequest = {
        tenantId,
        workArea: formState.workArea.trim(),
        name: formState.name.trim(),
        description: formState.description.trim() || undefined,
        isActive: formState.isActive,
      }
      let saved: PlotPlanDto
      if (editingItem) {
        saved = await updatePlotPlan(editingItem.plotPlanId, payload)
      } else {
        saved = await createPlotPlan(payload)
      }

      if (formState.file) {
        await uploadPlotPlanVersion({
          tenantId,
          plotPlanId: saved.plotPlanId,
          versionName: formState.versionName.trim() || undefined,
          remark: formState.remark.trim() || undefined,
          file: formState.file,
        })
      }

      toast.success("บันทึก Plot Plan สำเร็จ")
      handleDialogChange(false)
      loadData()
    } catch (error) {
      console.error(error)
      toast.error("บันทึก Plot Plan ไม่สำเร็จ")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggle = async (item: PlotPlanDto) => {
    if (!tenantId) return
    try {
      await updatePlotPlan(item.plotPlanId, {
        tenantId,
        workArea: item.workAreaCode ?? "",
        name: item.name ?? "",
        description: item.description ?? undefined,
        isActive: !item.isActive,
      })
      toast.success(item.isActive ? "ปิดการใช้งาน Plot Plan แล้ว" : "เปิดใช้งาน Plot Plan แล้ว")
      loadData()
    } catch (error) {
      console.error(error)
      toast.error("อัปเดตสถานะ Plot Plan ไม่สำเร็จ")
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between pb-2">
          <CardTitle className="text-lg">ค้นหา</CardTitle>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            เพิ่ม Plot Plan
          </Button>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1">
            <Label>คำค้นหา</Label>
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input
                className="pl-8"
                placeholder="ชื่อแผนผัง / หมายเหตุ"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Work Area</Label>
            <Select value={workAreaFilter} onValueChange={(value) => setWorkAreaFilter(value)}>
              <SelectTrigger>
                <SelectValue placeholder="เลือก Work Area" />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 pb-2 pt-1">
                  <Input
                    value={workAreaFilterSearch}
                    onChange={(e) => setWorkAreaFilterSearch(e.target.value)}
                    placeholder="ค้นหา Work Area"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
                {filteredWorkAreasFilter.length === 0 ? (
                  <div className="px-2 py-2 text-xs text-gray-500">ไม่พบข้อมูล</div>
                ) : (
                  filteredWorkAreasFilter.map((area) => (
                    <SelectItem key={area.locationId} value={area.code}>
                      {area.code} - {area.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>สถานะ</Label>
            <Select value={statusFilter} onValueChange={(value: PlotPlanStatusFilter) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue placeholder="ทั้งหมด" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="active">ใช้งาน</SelectItem>
                <SelectItem value="inactive">ไม่ใช้งาน</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">รายการ Plot Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ชื่อแผนผัง</TableHead>
                  <TableHead>Work Area</TableHead>
                  <TableHead>เวอร์ชันล่าสุด</TableHead>
                  <TableHead>สถานะ</TableHead>
                  <TableHead className="w-24 text-right">จัดการ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500">
                      {isLoading ? "กำลังโหลด..." : "ไม่พบข้อมูล"}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.plotPlanId}>
                      <TableCell>
                        <div className="font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.description ?? "-"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-900">{item.workAreaCode}</div>
                        <div className="text-xs text-gray-500">{item.workAreaName}</div>
                      </TableCell>
                      <TableCell>
                        {item.latestVersion ? (
                          <span className="text-sm text-gray-800">v{item.latestVersion}</span>
                        ) : (
                          <span className="text-xs text-gray-400">ยังไม่มีไฟล์</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={item.isActive ? "default" : "outline"}
                          className={item.isActive ? "bg-green-100 text-green-700" : ""}
                        >
                          {item.isActive ? "ใช้งาน" : "ไม่ใช้งาน"}
                        </Badge>
                      </TableCell>
                      
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)} className="gap-1">
                          <Edit2 className="h-4 w-4" /> แก้ไข
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(item)} className="gap-1">
                          {item.isActive ? (
                            <ToggleRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-gray-500" />
                          )}
                          {item.isActive ? "ปิด" : "เปิด"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-gray-600">
            <div>
              แสดง {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalItems)} จาก {totalItems} รายการ
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span>จำนวนต่อหน้า</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  ก่อนหน้า
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                >
                  ถัดไป
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Dialog open={isDialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? "แก้ไข Plot Plan" : "เพิ่ม Plot Plan ใหม่"}</DialogTitle>
            <DialogDescription>กำหนด Work Area และอัปโหลดไฟล์แผนผังสำหรับใช้งานในหน้าขอ Permit</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-1">
              <Label>Work Area</Label>
              <Select
                value={formState.workArea}
                onValueChange={(value) => {
                  setFormState((prev) => ({ ...prev, workArea: value }))
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="เลือก Work Area" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-2 pt-1">
                    <Input
                      value={workAreaFormSearch}
                      onChange={(e) => setWorkAreaFormSearch(e.target.value)}
                      placeholder="ค้นหา Work Area"
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredWorkAreasForm.length === 0 ? (
                    <div className="px-2 py-2 text-xs text-gray-500">ไม่พบข้อมูล</div>
                  ) : (
                    filteredWorkAreasForm.map((area) => (
                      <SelectItem key={area.locationId} value={area.code}>
                        {area.code} - {area.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>ชื่อแผนผัง</Label>
              <Input value={formState.name} onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))} placeholder="เช่น Cooling Tower Plot" />
            </div>
            <div className="space-y-1">
              <Label>คำอธิบาย</Label>
              <Input
                value={formState.description}
                onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="รายละเอียดเพิ่มเติม"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>เวอร์ชัน</Label>
                <Input
                  value={formState.versionName}
                  onChange={(e) => setFormState((prev) => ({ ...prev, versionName: e.target.value }))}
                  placeholder="เช่น v1.0"
                />
              </div>
              <div className="space-y-1">
                <Label>หมายเหตุไฟล์</Label>
                <Input value={formState.remark} onChange={(e) => setFormState((prev) => ({ ...prev, remark: e.target.value }))} placeholder="ถ้ามี" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>อัปโหลดแผนผัง (ภาพ)</Label>
              {editingItem?.backgroundPath ? (
                <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                  <span className="text-gray-700">ไฟล์ปัจจุบัน: {getFileName(editingItem.backgroundPath) || "-"}</span>
                  <a
                    className="text-blue-600 hover:underline"
                    href={`${API_BASE_URL.replace(/\/$/, "")}${editingItem.backgroundPath}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    เปิดดู
                  </a>
                </div>
              ) : null}

              <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-md px-3 py-2 cursor-pointer hover:border-gray-400 transition">
                <UploadCloud className="h-5 w-5 text-gray-500" />
                <span className="text-sm text-gray-700">
                  {formState.file ? formState.file.name : "เลือกไฟล์แผนผัง (PNG/JPG)"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null
                    setFormState((prev) => ({ ...prev, file }))
                  }}
                />
              </label>
              {editingItem ? (
                <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm">
                  <div className="font-medium text-gray-700">เวอร์ชันเดิม</div>
                  {isVersionsLoading ? (
                    <div className="text-xs text-gray-500">กำลังโหลดเวอร์ชัน...</div>
                  ) : plotPlanVersions.length === 0 ? (
                    <div className="text-xs text-gray-500">ยังไม่มีเวอร์ชันก่อนหน้า</div>
                  ) : (
                    <div className="mt-2 max-h-40 overflow-y-auto pr-1">
                      <ul className="space-y-1">
                        {plotPlanVersions.map((version) => (
                          <li key={version.plotPlanVersionId} className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">
                              v{version.version ?? "-"} - {getFileName(version.backgroundPath) || "-"}
                            </span>
                            {version.backgroundPath ? (
                              <a
                                className="text-xs text-blue-600 hover:underline"
                                href={`${API_BASE_URL.replace(/\/$/, "")}${version.backgroundPath}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                เปิดดู
                              </a>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="isActive"
                checked={formState.isActive}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, isActive: checked }))}
              />
              <Label htmlFor="isActive">ใช้งาน</Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => handleDialogChange(false)} disabled={isSubmitting}>
              ยกเลิก
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default PlotPlanMaster

